import {Validator} from '@/type/validation/result';
import {半角カナ禁止, 半角数のみ, 半角英数のみ, 日付書式, 必須入力} from '@/type/validation/validator';
import moment from 'moment';

export interface オプション区分型 {
    値: string | null;
    ラベル: string;
}

export const 商品区分一覧: オプション区分型[] = [
    {値: '医薬品', ラベル: '医薬品'},
    {値: 'その他品', ラベル: 'その他品'},
    {値: null, ラベル: '指定なし'},
];

export const 検索並び順区分一覧: オプション区分型[] = [
    {値: '発注伝票番号', ラベル: '発注伝票番号'},
    {値: '商品名', ラベル: '商品名'},
    {値: '規格容量', ラベル: '規格・容量'},
    {値: 'メーカー名', ラベル: 'メーカー名'},
    {値: '仕入先', ラベル: '卸'},
];

export const 発注ステータス区分一覧: オプション区分型[] = [
    {値: '発注完了', ラベル: '発注完了'},
    {値: '部分入荷済', ラベル: '部分入荷済'},
    {値: null, ラベル: '指定なし'},
];

export interface 入庫候補発注検索条件型 {
    商品区分: string | null;
    並び順区分: string;
    卸コード: string | null;
    メーカー名: string | null;
    商品名カナ: string | null;
    発注日FROM: Date | null;
    発注日TO: Date | null;
    発注伝票番号FROM: number | null;
    発注伝票番号TO: number | null;
    納品指定日FROM: Date | null;
    納品指定日TO: Date | null;
    発注ステータス区分: string | null;
}

export interface 入庫候補発注検索結果型 {
    入庫候補発注検索結果レコード一覧: 入庫候補発注補検索結果レコード型[];
    アラートメッセージ: string;
}

export interface 発注指定入庫確定依頼型 {
    入庫入力一覧: 発注指定入庫入力型[];
    入庫先部署: string;
}

export interface 発注指定入庫入力型 {
    商品区分: string;
    メーカー名: string;
    商品名: string;
    規格容量: string;
    卸コード: string;
    卸名: string;
    発注日: Date;
    発注伝票番号: number;
    発注伝票行番号: number;
    入庫日付: Date;
    入力単価: number;
    入庫数: number;
    入庫遡及対象外区分: string;
    有効期限: Date;
    ロット番号: string;
    伝票番号: string;
    伝票行番号: number;
    納品指定日: Date;
    発注数: number;
    入庫残数: number;
    発注ステータス区分: string;

    発注更新バージョン: number;
    変更前有効期限: Date; // チェックに必要
    有効期限入力フラグ: string;
}

export interface 入庫候補発注補検索結果レコード型 {
    選択: boolean; // 結果表示用
    商品区分: string;
    メーカー名: string;
    商品名: string;
    規格容量: string;
    卸コード: string;
    卸名: string;
    発注日: Date;
    発注伝票番号: number;
    発注伝票行番号: number;
    入庫日付: Date;
    入庫単価: number;
    入庫数: number;
    入庫遡及対象外区分: string;
    有効期限: Date;
    ロット番号: string;
    卸伝票番号: string;
    卸伝票行番号: number;
    納品指定日: Date;
    発注数: number;
    入庫残数: number;
    発注ステータス区分: string;

    発注更新バージョン: number;
    変更前有効期限: Date; // チェックに必要
    有効期限入力フラグ: string;
}

export type 日付型 = Date | null;

export interface 入庫情報タブ入力型 {
    入庫伝票番号: string;
    入庫伝票日付: 日付型;
}

function 伝票番号桁数検証(value: string): boolean {
    if (!value) {
        return true;
    }
    return value.length <= 10;
}

function 未来日時は不可(value: Date): boolean {
    if (value === null) {
        return true;
    }
    if (!日付書式(value)) {
        return true;
    }
    const momentValue = moment(value);
    const momentNow = moment(new Date());
    return momentValue.isBefore(momentNow);
}

// バリデーション用のマップ
export const 入庫候補発注検索条件Validator: Validator[] = [
    {
        name: '商品名カナ',
        constraints: [
            {method: 半角カナ禁止, message: '商品名カナに半角カナが含まれています'},
            // {method: 三文字以上の入力が必要, message: '商品名カナは３文字以上の入力が必要です'},
        ],
    },
    {
        name: '発注日FROM',
        constraints: [
            {method: 日付書式, message: '発注日FROMの書式が不正です'},
        ],
    },
    {
        name: '発注日TO',
        constraints: [
            {method: 日付書式, message: '発注日TOの書式が不正です'},
        ],
    },
    {
        name: '発注伝票番号FROM',
        constraints: [
            {method: 伝票番号桁数検証, message: '発注伝票番号は10文字以下で入力してください'},
            {method: 半角数のみ, message: '発注伝票番号は半角英数字で入力してください'},
        ],
    },
    {
        name: '発注伝票番号TO',
        constraints: [
            {method: 伝票番号桁数検証, message: '発注伝票番号は10文字以下で入力してください'},
            {method: 半角数のみ, message: '発注伝票番号は半角数字で入力してください'},
        ],
    },
    {
        name: '納品指定日FROM',
        constraints: [
            {method: 日付書式, message: '納品指定日FROMの書式が不正です'},
        ],
    },
    {
        name: '納品指定日TO',
        constraints: [
            {method: 日付書式, message: '納品指定日TOの書式が不正です'},
        ],
    },
];

export const 入庫情報全指定バリデータマップ一覧: Validator[] = [
    {
        name: '入庫伝票番号',
        constraints: [
            {method: 半角英数のみ, message: '入庫伝票番号は半角英数字で入力してください'},
        ],
    },
    {
        name: '入庫伝票日付',
        constraints: [
            {method: 日付書式, message: '入庫伝票日付の書式が不正です'},
            {method: 未来日時は不可, message: '入庫伝票日付は未来の日付への変更はできません'},
        ],
    },
];
